// ObsidianTensionMapper — Tier-A "Obsidian Dossier" renderer for the
// tension-mapper component (the world's SECOND home-set pairing, after
// claim-evidence).
//
// Reuses the existing Obsidian toolkit registration (atmosphere + noir audio cue
// palette in lib/scenario-themes/) — no new world infra. Reframes tension-mapper
// as a NOIR EVIDENCE BOARD: factor index-cards are pinned along a brass tension
// rail strung between two poles; "FILE THE MAPPING" stamps the board and the
// detective's analysis is revealed under the desk lamp.
//
// State machine + onActivityResponse mirror TensionMapperSlide's `Student` exactly:
// `positions` (factorId → 0..100) + `submitted`; the continuum drop math is
// replicated verbatim; on submit it emits the EXACT
//   { activityType:'tension-mapper', mode:'survey',
//     responseData:{ placements:[{factorId,position}…] } }
// payload.

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { TensionMapperSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

const STYLE_ID = 'obsidian-tm-scoped-styles';

const INKBG = '#0b0b0d';
const CARD = '#efe7d4';
const CARDINK = '#2a2620';
const AMBER = '#d4682a';
const BRASS = '#b8924a';
const BONE = '#e8e2d6';
const RED = '#b3402f';

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.obstm`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.obstm { position: relative; }
.obstm * { box-sizing: border-box; }

.obstm .otm-kicker { font-family: "Courier Prime", monospace; text-transform: uppercase; letter-spacing: 0.4em; font-size: 0.7rem; color: ${BRASS}; }
.obstm .otm-rule { height: 1px; background: linear-gradient(90deg, transparent, ${BRASS}66, transparent); margin: 10px 0 0; }

/* aged index card */
.obstm .otm-card {
  background: linear-gradient(180deg, #f3ecda, ${CARD}); color: ${CARDINK};
  border: 1px solid rgba(42,38,32,0.4); border-radius: 2px;
  box-shadow: 0 10px 26px -12px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.5);
}
.obstm .otm-brief { position: relative; padding: 22px 26px; transform: rotate(-0.5deg); }
.obstm .otm-brief::before { content: ""; position: absolute; top: -9px; left: 50%; width: 90px; height: 22px; transform: translateX(-50%) rotate(-2deg); background: rgba(184,146,74,0.32); border-left: 1px dashed rgba(42,38,32,0.4); border-right: 1px dashed rgba(42,38,32,0.4); }
.obstm .otm-meta { font-family: "Courier Prime", monospace; font-size: 0.66rem; letter-spacing: 0.16em; text-transform: uppercase; color: #7a6f59; }

/* board */
.obstm .otm-board {
  position: relative; border-radius: 4px; min-height: 280px; padding: 28px 22px;
  background:
    repeating-linear-gradient(135deg, rgba(0,0,0,0.12) 0 2px, transparent 2px 6px),
    radial-gradient(120% 120% at 50% 0%, #211c14, #15110b);
  border: 1px solid rgba(184,146,74,0.3); box-shadow: inset 0 0 80px rgba(0,0,0,0.7);
}
.obstm .otm-board.over { border-color: ${AMBER}; box-shadow: inset 0 0 80px rgba(212,104,42,0.18); }

/* brass tension rail */
.obstm .otm-rail { position: absolute; left: 26px; right: 26px; top: 56%; height: 2px; background: linear-gradient(90deg, ${AMBER}, ${BRASS}, ${AMBER}); box-shadow: 0 0 10px rgba(212,104,42,0.5); border-radius: 2px; }
.obstm .otm-pole { position: absolute; top: calc(56% - 30px); font-family: "Courier Prime", monospace; font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: ${BRASS}; }
.obstm .otm-pole.a { left: 22px; }
.obstm .otm-pole.b { right: 22px; text-align: right; }
.obstm .otm-railcap { position: absolute; top: calc(56% - 7px); width: 12px; height: 12px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #f0c98a, ${BRASS}); box-shadow: 0 0 8px rgba(184,146,74,0.6); }
.obstm .otm-railcap.a { left: 20px; }
.obstm .otm-railcap.b { right: 20px; }

/* draggable factor card (pool) */
.obstm .otm-factor {
  font-family: "Spectral", Georgia, serif; font-size: 0.92rem; font-weight: 500; line-height: 1.2;
  background: linear-gradient(180deg, #f3ecda, ${CARD}); color: ${CARDINK};
  border: 1px solid rgba(42,38,32,0.4); border-radius: 2px; padding: 9px 12px; max-width: 220px;
  box-shadow: 0 8px 18px -10px rgba(0,0,0,0.8); cursor: grab; touch-action: none; user-select: none;
  position: relative;
}
.obstm .otm-factor::before { content: ""; position: absolute; top: -5px; left: 50%; transform: translateX(-50%); width: 8px; height: 8px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #ff6f5f, ${RED}); box-shadow: 0 1px 2px rgba(0,0,0,0.6); }
.obstm .otm-factor.dragging { cursor: grabbing; opacity: 0.45; }

/* placed card on the rail */
.obstm .otm-placed {
  position: absolute; max-width: 150px; font-family: "Spectral", serif; font-size: 0.74rem; font-weight: 500;
  background: linear-gradient(180deg, #f3ecda, ${CARD}); color: ${CARDINK};
  border: 1px solid rgba(42,38,32,0.45); border-radius: 2px; padding: 6px 9px;
  box-shadow: 0 8px 16px -8px rgba(0,0,0,0.85); cursor: grab; touch-action: none;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.obstm .otm-placed::before { content: ""; position: absolute; top: -5px; left: 50%; transform: translateX(-50%); width: 9px; height: 9px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #ff6f5f, ${RED}); box-shadow: 0 1px 3px rgba(0,0,0,0.7); z-index: 2; }
/* the pin string down to the rail */
.obstm .otm-placed::after { content: ""; position: absolute; left: 50%; top: 100%; width: 1px; height: 26px; background: linear-gradient(${RED}, rgba(179,64,47,0.2)); }
.obstm .otm-placed.dragging { cursor: grabbing; opacity: 0.5; }

.obstm .otm-stamp-btn {
  font-family: "Courier Prime", monospace; text-transform: uppercase; letter-spacing: 0.18em; font-size: 0.82rem; cursor: pointer;
  color: ${INKBG}; background: ${AMBER}; border: none; border-radius: 3px; padding: 13px 22px;
  box-shadow: 0 0 18px rgba(212,104,42,0.4); transition: transform .12s, box-shadow .12s, filter .12s;
}
.obstm .otm-stamp-btn:hover { transform: translateY(-1px); box-shadow: 0 0 26px rgba(212,104,42,0.6); filter: brightness(1.05); }
.obstm .otm-stamp-btn:active { transform: translateY(1px); }

.obstm .otm-analysis { animation: otmRise .5s cubic-bezier(.2,.7,.2,1) both; }
@keyframes otmRise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.obstm .otm-filed { position: absolute; top: 16px; right: 20px; font-family: "Courier Prime", monospace; font-size: 1rem; letter-spacing: 0.12em; color: ${RED}; border: 3px solid ${RED}; border-radius: 5px; padding: 5px 11px; transform: rotate(-11deg); opacity: 0.82; }

@media (prefers-reduced-motion: reduce) {
  .obstm .otm-analysis { animation-duration: .001s !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

// Rehydrate a positions map from the canonical { placements:[{factorId,position}] }
// payload (the only shape this renderer emits).
function parseSavedPositions(saved: Record<string, unknown> | null | undefined): Record<string, number> | null {
  if (!saved || !Array.isArray(saved.placements)) return null;
  const positions: Record<string, number> = {};
  for (const item of saved.placements) {
    if (item && typeof item === 'object') {
      const { factorId, position } = item as { factorId?: unknown; position?: unknown };
      if (typeof factorId === 'string' && typeof position === 'number') positions[factorId] = position;
    }
  }
  return Object.keys(positions).length > 0 ? positions : null;
}

export function ObsidianTensionMapper({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TensionMapperSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('obsidian');
  const headingFont = theme.fonts.heading;

  // ── State machine (mirrors TensionMapperSlide's Student) ─────────────────────
  // A saved {placements} payload is the end state — restore the submitted view.
  const restored = parseSavedPositions(savedResponse);
  const [positions, setPositions] = useState<Record<string, number>>(restored ?? {});
  const [submitted, setSubmitted] = useState(restored !== null);
  // Fires onComplete exactly once at the terminal submit (already done if rehydrated).
  const completedRef = useRef(restored !== null);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const placedCount = Object.keys(positions).length;
  const allPlaced = placedCount === data.factors.length && data.factors.length > 0;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (submitted) return;
      if (!event.over || event.over.id !== 'continuum') return;
      const factorId = String(event.active.id);
      const rect = event.over.rect;
      const activeRect = event.active.rect.current.translated;
      if (!rect || !activeRect) return;
      const itemCentreX = activeRect.left + activeRect.width / 2;
      const pct = ((itemCentreX - rect.left) / rect.width) * 100;
      const clamped = Math.max(0, Math.min(100, pct));
      setPositions((prev) => ({ ...prev, [factorId]: clamped }));
      play('select'); // pin-push click
    },
    [submitted, play],
  );

  // A placed factor can be unpinned back to the pool (mirrors the standard
  // TensionMapperSlide's PlacedFactor remove affordance).
  const removeFactor = useCallback(
    (factorId: string) => {
      if (submitted) return;
      setPositions((prev) => {
        const next = { ...prev };
        delete next[factorId];
        return next;
      });
      play('reset');
    },
    [submitted, play],
  );

  const submit = useCallback(() => {
    setSubmitted(true);
    play('commit'); // stamp thunk
    if (!reduceMotion) window.setTimeout(() => play('reveal'), 320); // cello drone under the reveal
    onActivityResponse?.({
      activityType: 'tension-mapper',
      mode: 'survey',
      responseData: {
        placements: data.factors
          .filter((f) => positions[f.id] !== undefined)
          .map((f) => ({ factorId: f.id, position: positions[f.id] })),
      },
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [data.factors, positions, onActivityResponse, onComplete, play, reduceMotion]);

  const poolFactors = data.factors.filter((f) => positions[f.id] === undefined);
  const placedFactors = data.factors.filter((f) => positions[f.id] !== undefined);

  return (
    <div className="obstm h-full w-full overflow-y-auto" style={{ background: INKBG, color: BONE, fontFamily: theme.fonts.body }}>
      <ThemeWorldAtmosphere worldId="obsidian" primary={AMBER} accent={BRASS} background={INKBG} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 5 }}>
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '76px 22px 100px' }}>
          {/* header */}
          <header style={{ textAlign: 'center', marginBottom: 22 }}>
            <span className="otm-kicker">◆ Case Board · The Tension File</span>
            <h1 style={{ fontFamily: headingFont, fontSize: 'clamp(2rem, 5.5vw, 3.4rem)', lineHeight: 1.04, color: BONE, margin: '12px 0 0', letterSpacing: '0.01em' }}>
              {data.title}
            </h1>
            {data.subtitle && <p style={{ color: '#9a907c', fontSize: '0.95rem', margin: '8px 0 0', fontStyle: 'italic' }}>{data.subtitle}</p>}
            <div className="otm-rule" />
          </header>

          {/* case brief */}
          <div className="otm-card otm-brief" style={{ marginBottom: 24 }}>
            <div className="otm-meta" style={{ marginBottom: 8 }}>Case Brief · weigh each force</div>
            <p style={{ fontFamily: headingFont, fontSize: '1.2rem', lineHeight: 1.3, margin: '0 0 14px' }}>{data.tension.statement}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <div style={{ fontFamily: '"Courier Prime", monospace', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: RED, marginBottom: 4 }}>← {data.tension.poleA.label}</div>
                <div style={{ fontSize: '0.84rem', color: '#5e5544' }}>{data.tension.poleA.description}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: '"Courier Prime", monospace', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: RED, marginBottom: 4 }}>{data.tension.poleB.label} →</div>
                <div style={{ fontSize: '0.84rem', color: '#5e5544' }}>{data.tension.poleB.description}</div>
              </div>
            </div>
          </div>

          <DndContext sensors={sensors} onDragStart={() => { if (!submitted) play('hover'); }} onDragEnd={handleDragEnd}>
            {/* factor pool */}
            {poolFactors.length > 0 && !submitted && (
              <div style={{ marginBottom: 16 }}>
                <div className="otm-meta" style={{ color: BRASS, marginBottom: 10 }}>Pin each factor along the rail</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {poolFactors.map((f) => (
                    <PoolFactor key={f.id} id={f.id} label={f.label} description={f.description} />
                  ))}
                </div>
              </div>
            )}

            {/* the board */}
            <Board poleA={data.tension.poleA.label} poleB={data.tension.poleB.label}>
              {placedFactors.map((f, placedIdx) => (
                <PlacedFactor
                  key={f.id}
                  id={f.id}
                  label={f.label}
                  description={f.description}
                  position={positions[f.id]}
                  verticalSlot={placedIdx % 3}
                  disabled={submitted}
                  onRemove={submitted ? undefined : () => removeFactor(f.id)}
                />
              ))}
            </Board>
          </DndContext>

          {!submitted && allPlaced && (
            <button type="button" className="otm-stamp-btn" style={{ marginTop: 18, width: '100%' }} onClick={submit}>
              ▣ File the mapping
            </button>
          )}

          {/* analysis */}
          {submitted && (
            <div className="otm-analysis" style={{ marginTop: 26 }}>
              <div className="otm-card" style={{ padding: '26px 28px', position: 'relative' }}>
                <div className="otm-filed">Filed</div>
                <div className="otm-meta" style={{ color: '#8a5a2a', marginBottom: 10 }}>▾ Investigator's analysis</div>
                <p style={{ fontFamily: headingFont, fontSize: '1.12rem', lineHeight: 1.5, color: CARDINK, margin: '0 0 16px' }}>{data.expertAnalysis.summary}</p>
                <ul style={{ margin: 0, paddingLeft: 18, color: CARDINK }}>
                  {data.expertAnalysis.insights.map((ins, i) => {
                    const factor = data.factors.find((f) => f.id === ins.factorId);
                    return (
                      <li key={i} style={{ fontSize: '0.92rem', lineHeight: 1.5, marginBottom: 8 }}>
                        <strong style={{ color: '#7a3d12' }}>{factor?.label ?? ins.factorId}:</strong> {ins.perspective}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {data.reflection?.prompt && (
                <div className="otm-card" style={{ padding: '22px 26px', marginTop: 14, textAlign: 'center' }}>
                  <div className="otm-meta" style={{ color: '#8a5a2a', marginBottom: 8 }}>For the record</div>
                  <p style={{ fontFamily: headingFont, fontSize: '1.05rem', fontStyle: 'italic', color: CARDINK, margin: 0 }}>{data.reflection.prompt}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function Board({ poleA, poleB, children }: { poleA: string; poleB: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'continuum' });
  return (
    <div ref={setNodeRef} className={`otm-board${isOver ? ' over' : ''}`}>
      <div className="otm-pole a">← {poleA}</div>
      <div className="otm-pole b">{poleB} →</div>
      <div className="otm-railcap a" />
      <div className="otm-rail" />
      <div className="otm-railcap b" />
      {children}
    </div>
  );
}

function PoolFactor({ id, label, description, disabled }: { id: string; label: string; description: string; disabled?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`otm-factor${isDragging ? ' dragging' : ''}`}
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined }}
      title={description}
    >
      {label}
    </div>
  );
}

// Cycled vertical offsets so chips placed near the same x stay legible
// (mirrors the standard renderer's PLACED_SLOT_TOPS) — never all at one top.
const PLACED_SLOT_TOPS = ['14%', '34%', '54%'];

function PlacedFactor({
  id,
  label,
  description,
  position,
  verticalSlot,
  disabled,
  onRemove,
}: {
  id: string;
  label: string;
  description: string;
  position: number;
  verticalSlot: number;
  disabled?: boolean;
  onRemove?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`otm-placed${isDragging ? ' dragging' : ''}`}
      style={{
        left: `${position}%`,
        top: PLACED_SLOT_TOPS[verticalSlot % PLACED_SLOT_TOPS.length],
        transform: transform ? `translate(-50%, 0) translate3d(${transform.x}px, ${transform.y}px, 0)` : 'translate(-50%, 0)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
      title={description}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {onRemove && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Return ${label} to the pool`}
          style={{
            flexShrink: 0,
            fontFamily: '"Courier Prime", monospace',
            fontWeight: 700,
            lineHeight: 1,
            color: RED,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
